import {SizeCharts_SizeSystem} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_charts.types';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {
  COLORS,
  ContentPopper,
  DropDownProps,
  FlyoutProps,
  GenericIconName,
  Icon,
  Text,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {useSelectedSizeSystem} from './useSelectedSizeSystem';

export const SizesSystemSelector: FC<{
  selectedOption: number;
  options: Array<SizeCharts_SizeSystem>;
  onClick: React.Dispatch<React.SetStateAction<number>>;
  LayoutComponent: React.FC<FlyoutProps> | React.FC<DropDownProps>;
  dataTestId?: string;
  openedStateKey: string;
}> = ({
  selectedOption,
  options,
  onClick,
  LayoutComponent,
  dataTestId = 'sizesSystemSelector',
  openedStateKey,
}) => {
  const {open, isOpen, cancel} = useOpenedStateManager(openedStateKey);

  const selectedSizeSystem = useSelectedSizeSystem(selectedOption, options);

  if (!options) return null;

  if (options.length === 1) {
    return (
      <OpenerWrapper
        $isDropDownOpener={false}
        data-testid={`${dataTestId}-oneOption`}
      >
        <Text size="px12" textAlignment="left">
          {selectedSizeSystem?.shortName}
        </Text>
      </OpenerWrapper>
    );
  }

  return (
    <StyledContentPopper
      LayoutComponent={LayoutComponent}
      onOpenerClick={open}
      openerContent={
        <OpenerWrapper $isDropDownOpener={true}>
          <Text size="px12" textAlignment="left">
            {selectedSizeSystem?.shortName}
          </Text>
        </OpenerWrapper>
      }
      onClose={cancel}
      data-testid={`${dataTestId}-contentPopper`}
      open={isOpen}
      bodyContent={
        <Wrapper>
          {options.map((option, index) => {
            return (
              <Option
                data-selected={index === selectedOption}
                key={`size-system-option-${index}`}
                data-testid={`${dataTestId}-option-${index}`}
                onClick={() => {
                  onClick(index);
                  cancel();
                }}
              >
                {index === selectedOption && (
                  <StyledIcon size="S" asset={GenericIconName.Checkmark} />
                )}
                {option.name}
              </Option>
            );
          })}
        </Wrapper>
      }
    />
  );
};

const Wrapper = styled.ul`
  display: flex;
  flex-direction: column;
`;

const Option = styled.li`
  align-items: center;
  cursor: pointer;
  display: flex;
  list-style: none;
  padding: 10px 27px 10px 2.5rem;
  position: relative;
  text-align: left;

  &[data-selected='true'] {
    background-color: ${COLORS.Grey4};
    font-weight: bold;
  }

  ${tabletAndUpLinaria(`
    background-color: ${COLORS.White};
    padding: 8px 27px 8px 15px;

    &:hover {
      background-color: ${COLORS.Grey4};
    }
  `)}
`;

const OpenerWrapper = styled.div<{$isDropDownOpener: boolean}>`
  max-width: ${props => (props.$isDropDownOpener ? '130px' : 'unset')};
  padding: 0.5rem 0 0.5rem 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  text-align: left;
`;

const StyledIcon = styled(Icon)`
  bottom: 0;
  left: 0.5rem;
  margin: auto;
  position: absolute;
  top: 0;

  ${tabletAndUpLinaria('display: none;')};
`;

const StyledContentPopper = styled(ContentPopper)`
  width: 100%;
`;
